 

import { WebSocketConnect, WebSocketConnectClass } from "@/socket";
import {
  setOnlineTeammates,
  removeOfflineTeammates,
  fetchProjectData,
  SET_TEAMMATE,
} from "@/store/projectData/projectDataActions";
import {
  fetchProjects,
  fetchProjectsOnUpdate,
} from "@/store/project/projectActions";
import { removeUserProject } from "@/store/auth/authActions";
import { toastI18n } from "@/components/common/Toast";
// import { processChannelTopicUpdate } from './projectProfileActions';
import {
  SET_TEAMMATE_ON_TOPIC,
  REMOVE_TEAMMATE_ON_TOPIC,
  SET_PROJECT_CHANNEL,
} from "./projectProfileConstants";

/**
 * The project is no longer ours: drop it from the store and say so.
 *
 * Deliberately the same sequence leaveProject runs on success, because it is the
 * same end state — the only difference is who initiated it. Two paths to the same
 * place would have to be kept in step forever.
 */
export function projectAccessLost(projectId) {
  return (dispatch) => {
    dispatch(fetchProjects());
    dispatch(fetchProjectData());
    dispatch(removeUserProject(projectId));
    dispatch(toastI18n("info", "RemovedFromProject"));
  };
}

export function initializeRebelProjectChannel(projectId) {
  return (dispatch, getState) => {
    const { user } = getState();
    const channelName = WebSocketConnectClass.makeChannelName(
      WebSocketConnectClass.REBEL_PROJECT_ROOM_LOBBY,
      projectId
    );
    const channel = WebSocketConnect.channel(user.get("jwt"), channelName);

    WebSocketConnect.joinOnce(channel)
      .receive("ok", () =>
        dispatch({ type: SET_PROJECT_CHANNEL, projectId, channel })
      )
      .receive("error", (error) => {
        // This thunk only ever runs for projects the store believes we are in, so
        // the server refusing the join means membership ended while we were away —
        // the removed member's own socket was disconnected, so the member_removed
        // broadcast below never reached them. Anything else is left alone rather
        // than treated as lost access.
        if (error?.reason === "unauthorized") {
          dispatch(projectAccessLost(projectId));
          return;
        }

        console.warn("error", error);
      });

    // This thunk is dispatched for the same project from two places
    // (fetchProjects and fetchProjectsOnUpdate), and the channel object is now
    // shared, so registering the handlers below twice would dispatch every
    // presence and topic event twice.
    if (!WebSocketConnect.claimWiring(channel)) {
      return;
    }

    channel.on("presence_state", (state) => {
      dispatch(setOnlineTeammates(state));
    });

    // Event sent from server that tells us there is diff of users in channel
    channel.on("presence_diff", (payload) => {
      if (payload.joins) {
        dispatch(setOnlineTeammates(payload.joins));
      }

      if (payload.leaves) {
        dispatch(removeOfflineTeammates(payload.leaves));
      }
    });

    // update project data
    channel.on("project_profile_updated", () => {
      dispatch(fetchProjectsOnUpdate());
    });

    // if a user is on a challenge topic
    channel.on("joined_project_topic", (payload) => {
      // if userid is not current users, add to user list
      dispatch({
        type: SET_TEAMMATE_ON_TOPIC,
        topicId: Number(payload.topicId),
        isEditing: payload.isEditing,
        userId: payload.userId,
      });
    });

    // if a user is on a challenge topic
    channel.on("editing_project_topic", (payload) => {
      // if userid is not current users, add to user list
      dispatch({
        type: SET_TEAMMATE_ON_TOPIC,
        topicId: Number(payload.topicId),
        isEditing: payload.isEditing,
        userId: payload.userId,
      });
    });

    // if a user leaves a topic
    channel.on("left_project_topic", (payload) => {
      dispatch({
        type: REMOVE_TEAMMATE_ON_TOPIC,
        userId: payload.userId,
        topicId: Number(payload.topicId),
      });
      // if userid is not current users, add to user list
      // if (payload.userId !== user.get('id')) {
      //   dispatch(removeTeammateOnTopic(payload.topicId, payload.userId));
      // }
    });

    // // when a topic is saved
    // channel.on('topic_updated', (payload) => {
    //   dispatch(processChannelTopicUpdate(payload));
    // });

    // when a user in the project has been updated
    channel.on("user_project_role_updated", (payload) => {
      dispatch({ type: SET_TEAMMATE, data: payload.data });
    });

    // A member left or was removed. The roster is what everyone still here needs.
    // The `userId === us` branch is a backstop: ending a membership disconnects
    // that member's sockets, so in practice they are gone before this is sent.
    channel.on("member_removed", (payload) => {
      const currentUserId = getState().user.get("id");

      if (payload.userId === currentUserId) {
        dispatch(projectAccessLost(projectId));
        return;
      }

      dispatch(fetchProjectData());
    });
  };
}

export function broadcastJoinProjectChallengeTopic(projectId, topicId) {
  return (dispatch, getState) => {
    const channel = getState().projectProfilev1.projectChannels.get(
      parseInt(projectId, 10)
    );

    if (channel) {
      channel.push(`join:project_topic:${topicId}`, { topicId });
    }
  };
}

export function broadcastEditProjectChallengeTopic(projectId, topicId) {
  return (dispatch, getState) => {
    const channel = getState().projectProfilev1.projectChannels.get(
      parseInt(projectId, 10)
    );
    if (channel) {
      channel.push(`edit:project_topic:${topicId}`, { topicId });
    }
  };
}

export function broadcastLeaveProjectChallengeTopic(projectId, topicId) {
  return (dispatch, getState) => {
    const channel = getState().projectProfilev1.projectChannels.get(
      parseInt(projectId, 10)
    );
    if (channel) {
      channel.push(`leave:project_topic:${topicId}`, { topicId });
    }
  };
}
//
